import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { AIAssistant } from "../../components/ai/AIAssistant";
import {
  Shield,
  LogOut,
  Plus,
  DollarSign,
  FileText,
  Clock,
  TrendingUp,
  ArrowUpRight,
  Menu,
  X,
  Settings,
  AlertTriangle,
  CheckCircle,
  Search,
  Calendar,
  Home,
  Users,
  Briefcase,
  FileSearch,
  ShoppingBag
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Title Sidebar Component
export function TitleSidebar({ company, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);

  const navItems = [
    { icon: Home, label: "Dashboard", path: "/title/dashboard" },
    { icon: FileText, label: "Orders", path: "/title/orders" },
    { icon: ShoppingBag, label: "Marketplace", path: "/title/marketplace" },
    { icon: FileSearch, label: "Title Searches", path: "/title/searches" },
    { icon: Calendar, label: "Closings", path: "/title/closings" },
    { icon: Users, label: "Service Requests", path: "/title/requests" },
    { icon: DollarSign, label: "Revenue", path: "/title/revenue" },
    { icon: TrendingUp, label: "Analytics", path: "/title/analytics" },
    { icon: Settings, label: "Settings", path: "/title/settings" }
  ];

  return (
    <aside className={`fixed left-0 top-0 h-full bg-black border-r border-white/[0.06] transition-all z-40 ${
      collapsed ? "w-16" : "w-64"
    }`}>
      {/* Header */}
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        {!collapsed && (
          <Link to="/title/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Shield className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
            </div>
            <span className="font-heading font-bold text-white">TITLE</span>
          </Link>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-white/50 hover:text-white p-1"
          data-testid="title-sidebar-toggle"
        >
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="p-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = window.location.pathname === item.path;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 transition-all duration-300 ${
                isActive
                  ? "bg-emerald-500/10 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-l-2 border-emerald-500"
                  : "text-white/50 hover:text-white hover:bg-[#0A0A0A]/80 backdrop-blur-sm"
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User & Logout */}
      {!collapsed && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full flex items-center justify-center">
              <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-bold">
                {company?.company_name?.charAt(0) || "T"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{company?.company_name}</p>
              <p className="text-white/40 text-xs truncate">{company?.contact_name}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-[#FF3B30] py-2 border border-white/[0.08] hover:border-red-500/50 transition-all duration-300"
            data-testid="title-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      )}
    </aside>
  );
}

// Stat Card Component
function StatCard({ label, value, subtext, icon: Icon, color = "white", link }) {
  const content = (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-5 hover:border-emerald-500/30 transition-all duration-300">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 flex items-center justify-center ${
          color === "emerald" ? "bg-emerald-500/10 border-emerald-500/30" :
          color === "green" ? "bg-green-500/10 border-green-500/30" :
          color === "blue" ? "bg-blue-500/10 border-blue-500/30" :
          color === "amber" ? "bg-amber-500/10 border-amber-500/30" :
          "bg-white/[0.08]/50 border-white/[0.1]"
        } border`}>
          <Icon className={`w-5 h-5 ${
            color === "emerald" ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
            color === "green" ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
            color === "blue" ? "text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" :
            color === "amber" ? "text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" :
            "text-white/50"
          }`} />
        </div>
        {link && <ArrowUpRight className="w-4 h-4 text-white/40" />}
      </div>
      <div className={`font-mono text-2xl font-bold mb-1 ${
        color === "emerald" ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
        color === "green" ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
        "text-white"
      }`}>
        {value}
      </div>
      <div className="text-white/50 text-sm">{label}</div>
      {subtext && <div className="text-white/40 text-xs mt-1">{subtext}</div>}
    </div>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
}

// Stage Badge Component
const STAGE_COLORS = {
  order_received: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/30",
  title_search: "bg-purple-500/20 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] border-purple-500/30",
  search_review: "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/30",
  commitment_issued: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  requirements_pending: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  clear_to_close: "bg-green-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-green-500/30",
  closing_scheduled: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/30",
  closing_complete: "bg-teal-500/20 text-teal-400 border-teal-500/30",
  policy_issued: "bg-white/20/20 text-white/50 border-white/[0.15]/30"
};

const STAGE_LABELS = {
  order_received: "Order Received",
  title_search: "Title Search",
  search_review: "Search Review",
  commitment_issued: "Commitment Issued",
  requirements_pending: "Requirements Pending",
  clear_to_close: "Clear to Close",
  closing_scheduled: "Closing Scheduled",
  closing_complete: "Closing Complete",
  policy_issued: "Policy Issued"
};

function StageBadge({ stage }) {
  return (
    <span className={`px-2 py-1 text-xs font-medium border ${STAGE_COLORS[stage] || "bg-white/[0.08] text-white/70 border-white/[0.1]"}`}>
      {STAGE_LABELS[stage] || stage}
    </span>
  );
}

export default function TitleDashboardPage() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [attentionItems, setAttentionItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, statsRes, ordersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/title/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/title/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/title/orders`, { withCredentials: true })
      ]);

      setCompany(profileRes.data);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
      setAttentionItems(statsRes.data?.attention_needed || []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/title");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/title/auth/logout`, {}, { withCredentials: true });
      navigate("/title");
    } catch (error) {
      navigate("/title");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="title-dashboard">
      <TitleSidebar company={company} onLogout={handleLogout} />

      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">Dashboard</h1>
            <p className="text-white/50 mt-1">Welcome, {company?.company_name}</p>
          </div>
          <Link to="/title/orders/new">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-none" data-testid="new-order-btn">
              <Plus className="w-5 h-5 mr-2" /> New Order
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Active Orders"
            value={stats?.orders?.active || 0}
            icon={FileText}
            color="blue"
            link="/title/orders"
          />
          <StatCard
            label="Closings This Week"
            value={stats?.closings?.this_week || 0}
            icon={Calendar}
            color="emerald"
            link="/title/closings"
          />
          <StatCard
            label="MTD Revenue"
            value={formatCurrency(stats?.revenue?.mtd)}
            icon={DollarSign}
            color="green"
          />
          <StatCard
            label="Pending Revenue"
            value={formatCurrency(stats?.revenue?.pending)}
            icon={TrendingUp}
            color="amber"
          />
        </div>

        {/* Pipeline Overview */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 mb-6">
          <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Order Pipeline</h2>
          <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
            {["order_received", "title_search", "search_review", "commitment_issued", "clear_to_close"].map((stage) => (
              <Link
                key={stage}
                to={`/title/orders?stage=${stage}`}
                className="bg-black border border-white/[0.08] p-4 hover:border-emerald-500/30 transition-all duration-300 text-center"
              >
                <div className="font-mono text-2xl font-bold text-white mb-1">
                  {stats?.orders?.by_stage?.[stage] || 0}
                </div>
                <div className="text-white/50 text-xs">{STAGE_LABELS[stage]}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Orders */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-white uppercase">Recent Orders</h2>
              <Link to="/title/orders" className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] text-sm hover:underline">
                View All
              </Link>
            </div>

            {recentOrders.length > 0 ? (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <Link
                    key={order.order_id}
                    to={`/title/orders/${order.order_id}`}
                    className="flex items-center justify-between p-3 bg-black hover:border-emerald-500/30 border border-transparent transition-all duration-300"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{order.property_address}</p>
                      <p className="text-white/50 text-sm">
                        {order.buyer_name} • {order.order_type}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <StageBadge stage={order.stage} />
                      <p className="text-white/40 text-xs mt-1">
                        {formatCurrency(order.sale_price)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-white/30 mx-auto mb-2" />
                <p className="text-white/50">No orders yet</p>
                <Link to="/title/orders/new" className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] text-sm hover:underline">
                  Create your first order
                </Link>
              </div>
            )}
          </div>

          {/* Attention Needed */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-white uppercase">Needs Attention</h2>
              <span className="text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] text-sm">{attentionItems.length} items</span>
            </div>

            {attentionItems.length > 0 ? (
              <div className="space-y-3">
                {attentionItems.map((item) => (
                  <Link
                    key={item.order_id}
                    to={`/title/orders/${item.order_id}`}
                    className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 transition-all duration-300"
                  >
                    <AlertTriangle className="w-5 h-5 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{item.property_address}</p>
                      <p className="text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] text-sm">
                        {item.days_in_stage} days in "{STAGE_LABELS[item.stage]}"
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-emerald-500/50 mx-auto mb-2" />
                <p className="text-white/50">All orders on track</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/title/orders/new" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-emerald-500/30 transition-all duration-300 text-center">
            <Plus className="w-8 h-8 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] mx-auto mb-2" />
            <p className="text-white text-sm">New Order</p>
          </Link>
          <Link to="/title/requests" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-emerald-500/30 transition-all duration-300 text-center">
            <Users className="w-8 h-8 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] mx-auto mb-2" />
            <p className="text-white text-sm">Service Requests</p>
          </Link>
          <Link to="/title/closings" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-emerald-500/30 transition-all duration-300 text-center">
            <Calendar className="w-8 h-8 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] mx-auto mb-2" />
            <p className="text-white text-sm">Schedule Closing</p>
          </Link>
          <Link to="/title/analytics" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-emerald-500/30 transition-all duration-300 text-center">
            <TrendingUp className="w-8 h-8 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] mx-auto mb-2" />
            <p className="text-white text-sm">Analytics</p>
          </Link>
        </div>
      </main>

      {/* AI Assistant - Floating */}
      <AIAssistant userRole="title" variant="floating" />
    </div>
  );
}
